import { Button, Flex, Image, Typography } from "@qws-org/ui-kit";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";

import type { SecondaryLayoutData } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";
import { useAction } from "~/widgets/use-action";

const COOKIES_KEY = "cookies-notice-seen";

type LayoutNotificationProps = { data: SecondaryLayoutData };

export const SecondaryLayoutNotification: FC<
  PropsWithChildren<LayoutNotificationProps>
> = ({ data }) => {
  const { getDirectusImage } = useDirectus();
  const { callAction: onCallAction } = useAction("notification");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isSeen = window.localStorage.getItem(COOKIES_KEY);
    if (!isSeen) {
      setMounted(true);
    }
  }, []);

  const handleCloseCookies = (): void => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(COOKIES_KEY, "1");
    }
    setMounted(false);
  };
  if (!mounted || !data?.notification) return null;
  const messageIcon = data?.notification?.message_icon;
  const messageIconWidth = messageIcon?.mobile_image_width ?? undefined;
  const messageIconHeight = messageIcon?.mobile_image_height ?? undefined;
  const messageText = data?.notification?.translations?.[0]?.message_text;
  const messageActionText =
    data?.notification?.translations?.[0]?.message_action_text;
  const closeIcon = data?.notification?.close_icon;
  const closeIconWidth = closeIcon?.mobile_image_width ?? undefined;
  const closeIconHeight = closeIcon?.mobile_image_height ?? undefined;
  const messageAction = data?.notification?.message_action;
  const controlAction = messageAction?.action?.[0]?.item;

  let href: string | undefined;
  if (isExternalLink(controlAction)) {
    if (isTemplateUrl(controlAction)) {
      href = undefined;
    } else {
      href = controlAction.href ?? undefined;
    }
  } else if (isInternalLink(controlAction)) {
    href = controlAction.page ? `${controlAction.page.id}` : undefined;
  }

  const onPress = (): void => {
    if (messageAction) {
      onCallAction(messageAction);
    }
  };

  return (
    <Flex
      width="fit-content"
      bg="background-secondary-transparent"
      align="center"
      position={{
        type: "fixed",
        bottom: 64,
        left: 0,
        right: 0,
        zIndex: 29,
      }}
      gap={8}
      border={{ radius: "card" }}
      m={{ my: 0, mx: "auto" }}
      p={{
        py: 12,
        px: 12,
      }}
      animation={{
        name: "frameAppear",
      }}
    >
      {messageIcon && (
        <Image
          value={getDirectusImage(messageIcon?.image?.id ?? "", {
            format: "webp",
            width: messageIconWidth ? messageIconWidth * 2 : undefined,
            height: messageIconHeight ? messageIconHeight * 2 : undefined,
            quality: 70,
          })}
          image={{
            alt: messageIcon?.alternative_text ?? "",
          }}
          width={messageIconWidth}
          height={messageIconHeight}
        />
      )}
      <Flex
        align="center"
        color="text-primary-default"
        fontSize="body.mobile.medium"
        fontWeight="mobile.normal"
        lineHeight="body.mobile.medium"
        gap={8}
      >
        <Typography>{messageText}</Typography>
        <Button
          href={href}
          onPress={onPress}
          variant="tertiary"
          p={{ py: 0, px: 0 }}
        >
          <Typography color="text-selection">{messageActionText}</Typography>
        </Button>
      </Flex>
      <Image
        value={getDirectusImage(closeIcon?.image?.id ?? "", {
          format: "webp",
          width: closeIconWidth ? closeIconWidth * 2 : undefined,
          height: closeIconHeight ? closeIconHeight * 2 : undefined,
          quality: 70,
        })}
        onClick={handleCloseCookies}
        image={{
          alt: closeIcon?.alternative_text ?? "",
        }}
        width={closeIconWidth}
        height={closeIconHeight}
      />
    </Flex>
  );
};
